import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import type { PendingGeneration } from '@/types';

import useWardrobeStore from './wardrobe';
import type { IWardrobeStore } from './wardrobe';

interface ICreditsStore {
  toolPrice: number;
  availableCredits: number;
}

export interface IAppStore extends IWardrobeStore, ICreditsStore {}

interface PersistedSlice {
  library: { pendingGenerations: PendingGeneration[] };
}

const _store = create<IAppStore>()(
  persist(
    (...props) => ({
      ...useWardrobeStore(...props),
      toolPrice: 0,
      availableCredits: 0,
    }),
    {
      name: 'ai-wardrobe-pending',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state): PersistedSlice => ({
        library: { pendingGenerations: state.library.pendingGenerations },
      }),
      merge: (persisted, current) => {
        const p = persisted as PersistedSlice | undefined;
        const restored = p?.library?.pendingGenerations ?? [];
        return {
          ...current,
          library: { ...current.library, pendingGenerations: restored },
        };
      },
    },
  ),
);

// Wrap with useShallow so object-returning selectors don't trigger infinite re-renders
export function useAppStore<T>(selector: (s: IAppStore) => T): T {
  return _store(useShallow(selector));
}

export const getAppStore = _store.getState;
export const setAppStore = _store.setState;
